<template>
  <header class="nav" :class="{ scrolled: scrolled }">
    <div class="container nav-inner">
      <router-link class="logo" to="/">
        <img class="logo-img" :src="navLogo" alt="动态时序科技" />
      </router-link>

      <!-- 接口检索（沿用旧站能力与跳转地址） -->
      <div class="nav-search">
        <input
          class="nav-search-input"
          type="text"
          placeholder="请输入接口名称"
          v-model="keyword"
          @keyup.enter="goSearch"
        />
        <button class="nav-search-btn" type="button" aria-label="搜索接口" @click="goSearch">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
               stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="6.5"></circle>
            <path d="M16 16l4.5 4.5"></path>
          </svg>
        </button>
      </div>

      <nav class="nav-links">
        <router-link
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :exact="item.exact"
        >{{ item.name }}</router-link>
      </nav>

      <div class="nav-auth">
        <div class="auth-pill" v-if="!loginStatus">
          <a href="javascript:;" class="auth-pill-login" @click="openAuth('password')">登录</a>
          <span class="auth-pill-split" aria-hidden="true"></span>
          <a href="javascript:;" class="auth-pill-reg" @click="openAuth('register')">注册</a>
        </div>
        <div class="nav-account" v-else>
          <span class="nav-user" :title="phone">
            <span class="nav-user-avatar">{{ avatarText }}</span>
            {{ displayName }}
          </span>
          <span class="nav-acc-split" aria-hidden="true"></span>
          <a href="javascript:;" class="nav-logout" @click="logout">退出</a>
        </div>
      </div>

      <button
        class="nav-burger"
        :class="{ open: drawerOpen }"
        type="button"
        :aria-label="drawerOpen ? '关闭菜单' : '打开菜单'"
        :aria-expanded="drawerOpen ? 'true' : 'false'"
        aria-controls="navDrawer"
        @click="drawerOpen = !drawerOpen"
      >
        <span></span><span></span><span></span>
      </button>
    </div>

    <div class="nav-drawer" :class="{ open: drawerOpen }" id="navDrawer">
      <nav class="nav-drawer-links">
        <router-link
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :exact="item.exact"
          @click.native="drawerOpen = false"
        >{{ item.name }}</router-link>
      </nav>
    </div>

    <!-- 旧站全局遮罩：登录弹窗打开时、或页面通过 setOtherMeng 打开自身弹窗时显示 -->
    <div
      class="nav-model"
      v-show="$store.state.showLogin || $store.state.otherMengban"
    ></div>
  </header>
</template>

<script>
/* 全站顶部导航
   结构与交互形态移植自 website-handoff（透明导航滚动变色、右上登录/注册胶囊、
   ≤620px 汉堡抽屉），但**搜索跳转、登录态判定、退出登录均沿用本项目原有实现**，
   未新增或修改任何接口。 */
import { authApi } from '@/api';

export default {
  name: 'SiteNav',
  data() {
    return {
      scrolled: false,
      drawerOpen: false,
      keyword: '',
      loginStatus: false,
      name: '',
      // 品牌 logo：公司原始 logo（未做任何改绘，也不改用 handoff 的 brand/*.svg）。
      // 浅色底用彩色版（蓝色标识方块 + 灰色字标）；深色底用白色变体
      // （蓝色标识方块保持蓝色，仅字标转白），保证 logo 背景始终是品牌蓝。
      logoImg: require('../assets/logo.png'),
      logoImgDark: require('../assets/logo-inverse.png'),
      navItems: [
        { name: '首页', to: '/', exact: true },
        // 导航顺序对齐 website-handoff：首页 / 技术服务 / 数据产品 / 关于我们 / 联系我们 / 充值中心
        // 「充值中心」保持在主导航最右，紧邻右上登录/注册模块
        { name: '技术服务', to: '/consulting' },
        { name: '数据产品', to: '/api' },
        { name: '关于我们', to: '/aboutUs' },
        { name: '联系我们', to: '/contact' },
        { name: '充值中心', to: '/charge' }
      ]
    };
  },
  computed: {
    isHome() {
      return this.$route.path === '/';
    },
    // 首页未滚动的透明导航叠在深色 Hero 上：走深色底变体，保留蓝色标识方块
    navLogo() {
      return this.isHome && !this.scrolled ? this.logoImgDark : this.logoImg;
    },
    displayName() {
      const na = this.$store.state.userName || this.name || '';
      return na ? na.slice(0, 8) : '已登录';
    },
    avatarText() {
      const na = this.$store.state.userName || this.name || '';
      return na ? na.charAt(0).toUpperCase() : 'U';
    },
    phone() {
      const user = this.$cookies.getJSON('user');
      return (user && user.phone) || '';
    }
  },
  watch: {
    // 与原实现一致：搜索数据被清空时同步清空输入框
    '$store.state.searchData'(newVal) {
      if (!newVal) this.keyword = '';
    },
    // 路由切换时收起抽屉
    '$route.path'() {
      this.drawerOpen = false;
    }
  },
  mounted() {
    this.checkLogin();
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 60;
    },
    onResize() {
      if (window.innerWidth > 620) this.drawerOpen = false;
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.drawerOpen = false;
    },
    onDocumentClick(e) {
      if (!this.drawerOpen) return;
      const nav = this.$el;
      if (nav && !nav.contains(e.target)) this.drawerOpen = false;
    },
    openAuth(view) {
      // 先广播视图类型，再由 AuthModal 打开（顺序保证：弹窗打开时视图已就绪）
      window.dispatchEvent(new CustomEvent('dts:auth-open', { detail: { view } }));
    },
    checkLogin() {
      const user = this.$cookies.getJSON('user');
      if (user && user.token) {
        this.loginStatus = true;
        this.name = user.userName || '';
      } else {
        this.loginStatus = false;
      }
    },
    logout() {
      const user = this.$cookies.getJSON('user');
      this.$confirm('您确定要退出登录吗', { type: 'warning' })
        .then(() => {
          const params = {
            userId: user && user.userId
          };
          authApi.logout(params)
            .then((res) => {
              if (res.state === 200) {
                this.$message.success('退出登录成功');
                this.loginStatus = false;
                this.$cookies.remove('user');
                window.location.reload();
              } else {
                this.$message.error(res.message);
              }
            })
            .catch(err => {
              this.$message.error(err);
            });
        })
        .catch(() => {});
    },
    goSearch() {
      this.$store.commit('setSearchData', this.keyword);
      // 站点使用 history 路由，搜索直接跳到 /allApi（旧写的 /#/allApi 会被 index.html 兼容脚本重定向）
      const keyword = encodeURIComponent(this.keyword);
      window.open(`${location.origin}/allApi?search=${keyword}&source=search`, '_self');
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SiteNav.vue